import { t as tr } from '../i18n.js'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { api, ApiError, type GithubConnection, type GithubIssueRow, type GithubStatus, type ProjectDetail, type Priority } from '../api.js'
import { isClosed } from './activeOnly.js'

/**
 * Project → GitHub: connect one repository (fine-grained token, Issues:
 * read & write), choose how issue state maps onto this project's statuses,
 * and see the sync's health. Import and sync controls arrive in later slices.
 */
const PRIORITIES: Priority[] = ['low', 'medium', 'high', 'urgent']

export function GithubPanel({ d }: { d: ProjectDetail }) {
  const { project: p } = d
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['github', p.id], queryFn: () => api<GithubStatus>(`/projects/${p.id}/github`) })
  const refresh = () => qc.invalidateQueries({ queryKey: ['github', p.id] })
  if (q.isPending) return <p className="muted">{tr("Loading…")}</p>
  if (q.isError) return <p className="bad">{tr("Could not load the GitHub connection.")}</p>
  const s = q.data
  return (
    <div className="stack">
      {s.connection ? <Connected d={d} s={s} c={s.connection} onChange={refresh} /> : <ConnectForm d={d} onChange={refresh} />}
      {s.connection && !s.connection.disabledReason && <ImportPanel d={d} onChange={refresh} />}
      {s.connection && <RunsPanel d={d} />}
    </div>
  )
}

function useErr() {
  const [err, setErr] = useState<string | null>(null)
  const onError = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Request failed')
  return { err, setErr, onError }
}

function ConnectForm({ d, onChange, replacing, onDone }: { d: ProjectDetail; onChange: () => void; replacing?: boolean; onDone?: () => void }) {
  const { project: p } = d
  const { err, setErr, onError } = useErr()
  const [repository, setRepository] = useState('')
  const [token, setToken] = useState('')
  const [result, setResult] = useState<string | null>(null)
  const connect = useMutation({
    mutationFn: () => api<{ connection: GithubConnection; repo: { fullName: string; private: boolean; openIssues: number } }>(`/projects/${p.id}/github`, { method: 'PUT', json: { repository, token } }),
    onSuccess: (r) => { setResult(`${tr("Connected to")} ${r.repo.fullName} · ${r.repo.openIssues} ${tr("open issues")}`); setToken(''); onChange(); onDone?.() }, onError,
  })
  return (
    <form className="stack" onSubmit={(e) => { e.preventDefault(); setErr(null); setResult(null); connect.mutate() }}>
      {!replacing && <p className="muted small">{tr("Connect a repository to import its issues as tasks. When a task is finished here, the issue is closed on GitHub; when an issue is closed or reopened on GitHub, the task follows.")}</p>}
      <label>{tr("Repository")}<input value={repository} onChange={(e) => setRepository(e.target.value)} placeholder="owner/repo or https://github.com/owner/repo" required disabled={replacing} /></label>
      <label>{tr("Fine-grained personal access token")}<input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="github_pat_…" required minLength={20} autoComplete="off" />
        <span className="hint">{tr("GitHub → Settings → Developer settings → Fine-grained tokens. Limit it to this repository with permission Issues: Read and write (Metadata is included automatically). The token is verified now, stored encrypted, and never shown again.")}</span></label>
      {err && <p className="bad">{err}</p>}
      {result && <p className="good">{result}</p>}
      <div className="row">
        <button className="button" disabled={connect.isPending}>{connect.isPending ? tr("Checking…") : replacing ? tr("Replace token") : tr("Connect")}</button>
        {replacing && <button type="button" className="button" onClick={onDone}>{tr("Cancel")}</button>}
      </div>
    </form>
  )
}

function Connected({ d, s, c, onChange }: { d: ProjectDetail; s: GithubStatus; c: GithubConnection; onChange: () => void }) {
  const { project: p, permissions, statuses } = d
  const { err, setErr, onError } = useErr()
  const [replacing, setReplacing] = useState(false)
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [labelName, setLabelName] = useState('')
  const [labelPrio, setLabelPrio] = useState<Priority>('high')
  const patch = useMutation({ mutationFn: (json: Record<string, unknown>) => api(`/projects/${p.id}/github`, { method: 'PATCH', json }), onSuccess: onChange, onError })
  const test = useMutation({ mutationFn: () => api<{ ok: boolean; message: string; openIssues: number | null; rateLimitRemaining: number | null }>(`/projects/${p.id}/github/test`, { method: 'POST' }), onSuccess: (r) => { setTestMsg({ ok: r.ok, text: r.ok ? `${r.message} · ${r.openIssues} ${tr("open issues")}${r.rateLimitRemaining !== null ? ` · ${r.rateLimitRemaining} ${tr("API calls left this hour")}` : ''}` : r.message }); onChange() }, onError })
  const disconnect = useMutation({ mutationFn: () => api(`/projects/${p.id}/github`, { method: 'DELETE' }), onSuccess: onChange, onError })
  const qc = useQueryClient()
  const sync = useMutation({
    mutationFn: () => api<{ seen: number; updated: number; closed: number; reopened: number; errors: string[]; sent: number; failed: number }>(`/projects/${p.id}/github/sync`, { method: 'POST' }),
    onSuccess: (r) => { setTestMsg({ ok: r.errors.length === 0 && r.failed === 0, text: `${tr("Synced:")} ${r.seen} ${tr("checked")}, ${r.updated + r.closed + r.reopened} ${tr("updated here")}, ${r.sent} ${tr("sent to GitHub")}${r.failed ? `, ${r.failed} ${tr("failed")}` : ''}${r.errors.length ? ` — ${r.errors.join('; ')}` : ''}` }); onChange(); void qc.invalidateQueries({ queryKey: ['github-runs', p.id] }); void qc.invalidateQueries({ queryKey: ['tasks', p.id] }) }, onError,
  })
  const done = statuses.filter((x) => x.category === 'done'), open = statuses.filter((x) => !isClosed(x.category))
  const canEdit = permissions.update
  const setLabel = (name: string, prio: Priority | null) => {
    const next = { ...c.priorityLabels }
    if (prio) next[name] = prio; else delete next[name]
    patch.mutate({ priorityLabels: next })
  }
  return (
    <div className="stack">
      <div className="row between">
        <div>
          <strong><a href={c.htmlUrl} target="_blank" rel="noreferrer">{c.owner}/{c.repo}</a></strong>
          <span className="muted small"> · {tr("token")} ····{c.tokenLast4} · {c.linkedIssues} {tr("linked issues")}{c.pendingUpdates > 0 ? ` · ${c.pendingUpdates} ${tr("updates waiting")}` : ''}{c.lastPollAt ? ` · ${tr("last checked")} ${new Date(c.lastPollAt).toLocaleString()}` : ''}</span>
        </div>
        {canEdit && <div className="row">
          <button type="button" className="button" onClick={() => { setErr(null); setTestMsg(null); sync.mutate() }} disabled={sync.isPending || !!c.disabledReason}>{sync.isPending ? tr("Syncing…") : tr("Sync now")}</button>
          <button type="button" className="button" onClick={() => { setErr(null); setTestMsg(null); test.mutate() }} disabled={test.isPending}>{tr("Test connection")}</button>
          <button type="button" className="button" onClick={() => setReplacing((v) => !v)}>{tr("Replace token")}</button>
          <button type="button" className="button danger" onClick={() => { if (confirm(tr("Disconnect this repository? Existing issue links are kept; nothing is deleted on GitHub."))) disconnect.mutate() }}>{tr("Disconnect")}</button>
        </div>}
      </div>
      {c.disabledReason && <p className="bad">{tr("Sync is paused:")} {c.disabledReason}. {tr("Replace the token or test the connection to resume.")}</p>}
      {s.webhookConfigured
        ? <p className="muted small">{tr("Webhook: add")} <code>{window.location.origin}/api/integrations/github/webhook</code> {tr("to the repository with the server's GITHUB_WEBHOOK_SECRET, content type JSON, events: Issues, Pushes, Pull requests. Without it, changes still arrive by hourly polling or “Sync now”.")}</p>
        : <p className="muted small">{tr("GITHUB_WEBHOOK_SECRET is not set on the server, so changes made on GitHub reach APM by hourly polling or “Sync now” rather than instantly.")}</p>}
      {testMsg && <p className={testMsg.ok ? 'good' : 'bad'}>{testMsg.text}</p>}
      {replacing && <ConnectForm d={d} onChange={onChange} replacing onDone={() => setReplacing(false)} />}

      <h3 className="small">{tr("Status mapping")}</h3>
      <div className="row">
        <label><input type="checkbox" checked={c.pushStatus} disabled={!canEdit} onChange={(e) => patch.mutate({ pushStatus: e.target.checked })} /> {tr("Close and reopen issues when task status changes")}</label>
        <label><input type="checkbox" checked={c.syncLabels} disabled={!canEdit} onChange={(e) => patch.mutate({ syncLabels: e.target.checked })} /> {tr("Mirror the task status as an apm:… label")}</label>
      </div>
      <div className="row">
        <label style={{ flex: 1 }}>{tr("Issue closed as completed →")}
          <select value={c.doneStatusId ?? ''} disabled={!canEdit} onChange={(e) => patch.mutate({ doneStatusId: e.target.value || null })}>
            <option value="">{tr("first done status")}{done[0] ? ` (${done[0].name})` : ''}</option>
            {done.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select></label>
        <label style={{ flex: 1 }}>{tr("Issue closed as not planned →")}
          <select value={c.notPlannedStatusId ?? ''} disabled={!canEdit} onChange={(e) => patch.mutate({ notPlannedStatusId: e.target.value || null })}>
            <option value="">{tr("same as completed")}</option>
            {done.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <span className="hint">{tr("Moving a task into this status closes the issue as “not planned”; any other done status closes it as “completed”.")}</span></label>
        <label style={{ flex: 1 }}>{tr("Issue reopened →")}
          <select value={c.reopenStatusId ?? ''} disabled={!canEdit} onChange={(e) => patch.mutate({ reopenStatusId: e.target.value || null })}>
            <option value="">{tr("first to-do status")}{open[0] ? ` (${open[0].name})` : ''}</option>
            {open.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select></label>
      </div>

      <h3 className="small">{tr("Priority from labels")}</h3>
      <p className="muted small">{tr("When an imported issue carries one of these labels, the task gets the matching priority (first match wins).")}</p>
      {Object.keys(c.priorityLabels).length > 0 && (
        <ul className="plain small">
          {Object.entries(c.priorityLabels).map(([name, prio]) => <li key={name}><code>{name}</code> → <span className={`tag prio-${prio}`}>{prio}</span> {canEdit && <button type="button" className="link" onClick={() => setLabel(name, null)}>{tr("remove")}</button>}</li>)}
        </ul>
      )}
      {canEdit && <form className="row" onSubmit={(e) => { e.preventDefault(); if (labelName.trim()) { setLabel(labelName.trim(), labelPrio); setLabelName('') } }}>
        <input value={labelName} onChange={(e) => setLabelName(e.target.value)} placeholder={tr("label, e.g. bug")} maxLength={100} />
        <select value={labelPrio} onChange={(e) => setLabelPrio(e.target.value as Priority)}>{PRIORITIES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
        <button className="button">{tr("Add")}</button>
      </form>}
      {err && <p className="bad">{err}</p>}
    </div>
  )
}

function ImportPanel({ d, onChange }: { d: ProjectDetail; onChange: () => void }) {
  const { project: p } = d
  const qc = useQueryClient()
  const { err, setErr, onError } = useErr()
  const [state, setState] = useState<'open' | 'closed' | 'all'>('open')
  const [labels, setLabels] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [assign, setAssign] = useState(true)
  const [createMilestones, setCreateMilestones] = useState(true)
  const [result, setResult] = useState<string | null>(null)
  const issues = useQuery({
    queryKey: ['github-issues', p.id, state, labels, q, page], enabled: open,
    queryFn: () => api<{ issues: GithubIssueRow[]; page: number; hasMore: boolean; rateLimitRemaining: number | null }>(`/projects/${p.id}/github/issues?state=${state}&labels=${encodeURIComponent(labels)}&q=${encodeURIComponent(q)}&page=${page}`),
    retry: false,
  })
  const importable = (issues.data?.issues ?? []).filter((i) => i.action !== 'linked')
  const run = useMutation({
    mutationFn: () => api<{ created: number; updated: number; skipped: number; errors: { number: number; message: string }[] }>(`/projects/${p.id}/github/import`, { method: 'POST', json: { numbers: [...selected], assign, createMilestones } }),
    onSuccess: (r) => {
      setResult(`${r.created} ${tr("created")}, ${r.updated} ${tr("updated")}, ${r.skipped} ${tr("already linked")}${r.errors.length ? `; ${r.errors.length} ${tr("failed")}: ${r.errors.map((e) => `#${e.number} ${e.message}`).join(', ')}` : ''}`)
      setSelected(new Set()); onChange()
      void qc.invalidateQueries({ queryKey: ['github-issues', p.id] }); void qc.invalidateQueries({ queryKey: ['github-runs', p.id] }); void qc.invalidateQueries({ queryKey: ['tasks', p.id] }); void qc.invalidateQueries({ queryKey: ['project', p.id] })
    }, onError,
  })
  const toggle = (n: number) => setSelected((s) => { const next = new Set(s); if (next.has(n)) next.delete(n); else next.add(n); return next })
  const all = importable.length > 0 && importable.every((i) => selected.has(i.number))
  if (!open) return <div><button type="button" className="button" onClick={() => setOpen(true)}>{tr("Import issues…")}</button></div>
  return (
    <div className="stack">
      <h3 className="small">{tr("Import issues")}</h3>
      <form className="row" onSubmit={(e) => { e.preventDefault(); setPage(1); void issues.refetch() }}>
        <select value={state} onChange={(e) => { setState(e.target.value as typeof state); setPage(1) }}><option value="open">{tr("open")}</option><option value="closed">{tr("closed")}</option><option value="all">{tr("all")}</option></select>
        <input value={labels} onChange={(e) => setLabels(e.target.value)} placeholder={tr("labels, comma-separated")} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("search title or #number")} />
        <button className="button">{tr("Search")}</button>
      </form>
      {issues.isPending && <p className="muted">{tr("Loading…")}</p>}
      {issues.isError && <p className="bad">{(issues.error as ApiError).message}</p>}
      {issues.data && (
        <>
          {issues.data.issues.length === 0 ? <p className="muted">{tr("No issues match.")}</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table small">
                <thead><tr><th><input type="checkbox" checked={all} onChange={(e) => setSelected(e.target.checked ? new Set(importable.map((i) => i.number)) : new Set())} /></th><th>#</th><th>{tr("Title")}</th><th>{tr("Labels")}</th><th>{tr("Assignee")}</th><th>{tr("Milestone")}</th><th>{tr("Priority")}</th><th>{tr("Action")}</th></tr></thead>
                <tbody>
                  {issues.data.issues.map((i) => (
                    <tr key={i.number} className={i.action === 'linked' ? 'muted' : ''}>
                      <td>{i.action !== 'linked' && <input type="checkbox" checked={selected.has(i.number)} onChange={() => toggle(i.number)} />}</td>
                      <td><a href={i.url} target="_blank" rel="noreferrer">#{i.number}</a></td>
                      <td>{i.title}{i.state === 'closed' && <span className="tag">{i.stateReason === 'not_planned' ? tr("not planned") : tr("closed")}</span>}</td>
                      <td>{i.labels.join(', ')}</td>
                      <td>{i.assigneeName ?? (i.assignees.length ? <span className="muted" title={tr("No APM account has this GitHub username")}>{i.assignees.join(', ')}</span> : '—')}</td>
                      <td>{i.milestone ?? '—'}</td>
                      <td>{i.priority ? <span className={`tag prio-${i.priority}`}>{i.priority}</span> : '—'}</td>
                      <td>{i.action === 'linked' ? <Link to={`/projects/${p.id}?task=${i.taskId}`}>{i.taskKey}</Link> : i.action === 'update' ? <>{tr("update")} <Link to={`/projects/${p.id}?task=${i.taskId}`}>{i.taskKey}</Link></> : tr("create")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row between">
            <div className="row">
              <button type="button" className="link" disabled={page <= 1} onClick={() => setPage((x) => x - 1)}>‹ {tr("previous")}</button>
              <span className="muted small">{tr("page")} {issues.data.page}</span>
              <button type="button" className="link" disabled={!issues.data.hasMore} onClick={() => setPage((x) => x + 1)}>{tr("next")} ›</button>
            </div>
            {issues.data.rateLimitRemaining !== null && <span className="muted small">{issues.data.rateLimitRemaining} {tr("API calls left this hour")}</span>}
          </div>
        </>
      )}
      <div className="row">
        <label className="inline"><input type="checkbox" checked={assign} onChange={(e) => setAssign(e.target.checked)} /> {tr("Assign to matching people")}</label>
        <label className="inline"><input type="checkbox" checked={createMilestones} onChange={(e) => setCreateMilestones(e.target.checked)} /> {tr("Create missing milestones")}</label>
      </div>
      {err && <p className="bad">{err}</p>}
      {result && <p className="good">{result}</p>}
      <div className="row">
        <button type="button" className="button" disabled={selected.size === 0 || run.isPending} onClick={() => { setErr(null); setResult(null); run.mutate() }}>{run.isPending ? tr("Importing…") : `${tr("Import")} ${selected.size}`}</button>
        <button type="button" className="button" onClick={() => setOpen(false)}>{tr("Close")}</button>
      </div>
    </div>
  )
}

function RunsPanel({ d }: { d: ProjectDetail }) {
  const { project: p } = d
  const q = useQuery({ queryKey: ['github-runs', p.id], queryFn: () => api<{ runs: { id: number; kind: string; startedAt: string; finishedAt: string | null; summary: Record<string, unknown> }[]; pending: { id: number; taskKey: string; taskId: string; action: string; attempts: number; nextAttemptAt: string; lastError: string | null }[] }>(`/projects/${p.id}/github/runs`) })
  if (!q.data || (q.data.runs.length === 0 && q.data.pending.length === 0)) return null
  const fmtSummary = (s: Record<string, unknown>) => Object.entries(s).filter(([k, v]) => k !== 'errors' && typeof v === 'number' && v).map(([k, v]) => `${v} ${k}`).join(', ') + ((s.errors as unknown[] | undefined)?.length ? ` · ${(s.errors as unknown[]).length} ${tr("errors")}` : '')
  return (
    <details>
      <summary className="small">{tr("Sync history")} ({q.data.runs.length}){q.data.pending.length ? ` · ${q.data.pending.length} ${tr("updates waiting")}` : ''}</summary>
      {q.data.pending.length > 0 && <ul className="plain small">{q.data.pending.map((x) => <li key={x.id}><Link to={`/projects/${p.id}?task=${x.taskId}`}>{x.taskKey}</Link> {x.action}{x.attempts ? ` · ${tr("attempt")} ${x.attempts}` : ''}{x.lastError ? <span className="bad"> — {x.lastError}</span> : null}</li>)}</ul>}
      <ul className="plain small">{q.data.runs.map((r) => <li key={r.id}>{new Date(r.startedAt).toLocaleString()} · {r.kind} · {fmtSummary(r.summary) || (r.finishedAt ? tr("nothing to do") : tr("running"))}</li>)}</ul>
    </details>
  )
}
